export interface VideoDraft {
  id: string;
  threadId: string;
  videoBlob: Blob;
  thumbnailBlob: Blob | null;
  coverFrameTimeMs: number;
  coverSelectionMode: 'auto' | 'manual';
  capturedAt: number; // timestamp ms
  duration: number;   // seconds
}

const DB_NAME = 'storythreads-drafts';
const DB_VERSION = 1;
const STORE_NAME = 'video-drafts';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('threadId', 'threadId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function getDraftInternal(db: IDBDatabase, threadId: string): Promise<VideoDraft | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.objectStore(STORE_NAME).index('threadId');
    const request = index.get(threadId);
    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => reject(request.error);
  });
}

function deleteDraftInternal(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function saveDraft(draft: Omit<VideoDraft, 'id' | 'capturedAt'>): Promise<VideoDraft> {
  const db = await openDB();

  // Remove any existing draft for this thread (one draft per thread)
  const existing = await getDraftInternal(db, draft.threadId);
  if (existing) {
    await deleteDraftInternal(db, existing.id);
  }

  const fullDraft: VideoDraft = {
    ...draft,
    id: crypto.randomUUID(),
    capturedAt: Date.now(),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(fullDraft);
    tx.oncomplete = () => resolve(fullDraft);
    tx.onerror = () => reject(tx.error);
  });
}

export async function getDraft(threadId: string): Promise<VideoDraft | null> {
  const db = await openDB();
  return getDraftInternal(db, threadId);
}

export async function deleteDraft(id: string): Promise<void> {
  const db = await openDB();
  return deleteDraftInternal(db, id);
}

export async function deleteExpiredDrafts(maxAgeDays = 7): Promise<void> {
  const db = await openDB();
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        if (cursor.value.capturedAt < cutoff) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
